"use client";

import { Modal, ModalContent, ModalHeader, ModalBody, Button, Input, Textarea, RadioGroup, Radio, Select, SelectItem } from "@nextui-org/react";
import { useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api-client";

interface CreateChallengeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateChallengeModal({ isOpen, onClose }: CreateChallengeModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [image, setImage] = useState<string>();

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const formData = new FormData(e.currentTarget);
      
      if (!startDate || !endDate) {
        toast.error("Please select both start and end dates");
        return;
      }

      // Combine date with midnight time for full day challenges
      const startDateTime = new Date(`${startDate}T00:00:00`);
      const endDateTime = new Date(`${endDate}T23:59:59`);

      if (endDateTime <= startDateTime) {
        toast.error("End date must be after start date");
        return;
      }

      const data = {
        name: formData.get("name"),
        description: formData.get("description"),
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString(),
        goal: formData.get("goal"),
        goalMetric: formData.get("goalMetric"),
        image,
        visibility: formData.get("visibility"),
        difficulty: formData.get("difficulty"),
        category: formData.get("category"),
        reward: formData.get("reward") || null,
      };

      const response = await apiClient.createChallenge(data);

      toast.success("Challenge created successfully!");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      size="2xl"
      className="bg-secondary"
    >
      <ModalContent>
        <ModalHeader className="text-xl">Create New Challenge</ModalHeader>
        <ModalBody className="pb-6">
          <form onSubmit={onSubmit} className="space-y-6">
            <div className="space-y-4">
              <Input
                id="name"
                name="name"
                label="Challenge Name"
                variant="bordered"
                classNames={{
                  input: "bg-transparent"
                }}
                required
              />

              <Textarea
                id="description"
                name="description"
                label="Description"
                variant="bordered"
                classNames={{
                  input: "bg-transparent"
                }}
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="date"
                  label="Start Date"
                  value={startDate}
                  onChange={(e) => setStartDate(e?.target?.value)}
                  min={new Date().toISOString().split('T')[0]}
                  variant="bordered"
                  classNames={{
                    input: "bg-transparent"
                  }}
                />

                <Input
                  type="date"
                  label="End Date"
                  value={endDate}
                  onChange={(e) => setEndDate(e?.target?.value)}
                  min={startDate || new Date().toISOString().split('T')[0]}
                  variant="bordered"
                  classNames={{
                    input: "bg-transparent"
                  }}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  id="goal"
                  name="goal"
                  type="number"
                  label="Goal"
                  variant="bordered"
                  classNames={{
                    input: "bg-transparent"
                  }}
                  required
                />

                <Select
                  name="goalMetric"
                  label="Metric"
                  defaultSelectedKeys={["steps"]}
                  variant="bordered"
                  classNames={{
                    trigger: "bg-transparent"
                  }}
                >
                  <SelectItem key="steps" value="steps">Steps</SelectItem>
                  <SelectItem key="miles" value="miles">Miles</SelectItem>
                  <SelectItem key="minutes" value="minutes">Minutes</SelectItem>
                  <SelectItem key="calories" value="calories">Calories</SelectItem>
                  <SelectItem key="workouts" value="workouts">Workouts</SelectItem>
                </Select>
              </div>

              <div>
                <p className="text-sm mb-2">Challenge Image</p>
                <div className="flex items-center gap-x-3">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                  />
                  <Button
                    as="label"
                    htmlFor="image-upload"
                    variant="light"
                    className="cursor-pointer"
                    startContent={<Upload className="h-4 w-4" />}
                  >
                    Upload Image
                  </Button>
                  {image && (
                    <img src={image} alt="Preview" className="h-20 w-20 object-cover rounded-md" />
                  )}
                </div>
              </div>

              <div>
                <p className="text-sm mb-2">Visibility</p>
                <RadioGroup
                  name="visibility"
                  defaultValue="public"
                  orientation="horizontal"
                  classNames={{
                    wrapper: "gap-4"
                  }}
                >
                  <Radio value="public">Public</Radio>
                  <Radio value="private">Private</Radio>
                </RadioGroup>
              </div>

              <div>
                <p className="text-sm mb-2">Difficulty</p>
                <RadioGroup
                  name="difficulty"
                  defaultValue="medium"
                  orientation="horizontal"
                  classNames={{
                    wrapper: "gap-4"
                  }}
                >
                  <Radio value="easy">Easy</Radio>
                  <Radio value="medium">Medium</Radio>
                  <Radio value="hard">Hard</Radio>
                </RadioGroup>
              </div>

              <Select
                name="category"
                label="Category"
                defaultSelectedKeys={["fitness"]}
                variant="bordered"
                classNames={{
                  trigger: "bg-transparent"
                }}
              >
                <SelectItem key="fitness" value="fitness">Fitness</SelectItem>
                <SelectItem key="nutrition" value="nutrition">Nutrition</SelectItem>
                <SelectItem key="wellness" value="wellness">Wellness</SelectItem>
                <SelectItem key="mindfulness" value="mindfulness">Mindfulness</SelectItem>
                <SelectItem key="sleep" value="sleep">Sleep</SelectItem>
              </Select>

              <Input
                id="reward"
                name="reward"
                label="Reward (optional)"
                placeholder="Enter reward description"
                variant="bordered"
                classNames={{
                  input: "bg-transparent"
                }}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="light"
                onPress={onClose}
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
                isDisabled={isLoading}
              >
                {isLoading ? "Creating..." : "Create Challenge"}
              </Button>
            </div>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}